import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Typography, Stack, styled } from '@mui/material';
import { theme } from '~/shared/styles/theme';
import { ROUTES } from '~/shared/constants';
import {
  ConsentCheckbox,
  PrimaryButton,
  OnboardingLayout,
} from '../components';
import { useAuth0 } from '@auth0/auth0-react';
import { handleLogout as handleLogoutUtil } from '~/shared/utils/auth';

interface AcknowledgmentFormData {
  acknowledgment: boolean;
}

export const EducationalAcknowledgmentScreen = () => {
  const navigate = useNavigate();
  const { logout } = useAuth0();

  const { control, watch, handleSubmit } = useForm<AcknowledgmentFormData>({
    defaultValues: {
      acknowledgment: false,
    },
  });

  const acknowledgmentChecked = watch('acknowledgment');

  const onSubmit = async () => {
    if (!acknowledgmentChecked) {
      return;
    }

    // Navigate directly to questions - no database mutation needed
    navigate(ROUTES.ONBOARDING.QUESTIONS);
  };

  const handleLogOut = () => {
    handleLogoutUtil(logout);
  };

  return (
    <OnboardingLayout
      showProgress={true}
      progressPercentage={0}
      showLogOut={true}
      onLogOut={handleLogOut}
    >
      <FormStack component="form" onSubmit={handleSubmit(onSubmit)}>
        <ContentStack>
          <TitleTypography>Could your family be at risk?</TitleTypography>

          <DescriptionTypography>
            Next you will complete a non-medical form to help identify whether
            you or your relatives might be at risk for OTC deficiency, a rare
            genetic condition.
          </DescriptionTypography>

          <DisclaimerTypography>
            This is for educational purposes only — only a licensed physician
            can confirm eligibility for testing.
          </DisclaimerTypography>

          <ConsentCheckbox
            name="acknowledgment"
            control={control}
            label="I understand this form is for educational and awareness purposes only."
          />
        </ContentStack>

        <ButtonStack>
          <PrimaryButton
            type="submit"
            variant="green"
            disabled={!acknowledgmentChecked}
          >
            Start Questionnaire
          </PrimaryButton>
        </ButtonStack>
      </FormStack>
    </OnboardingLayout>
  );
};

const FormStack = styled(Stack)(() => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  justifyContent: 'space-between',
})) as typeof Stack;

const ContentStack = styled(Stack)({
  alignItems: 'center',
  marginBottom: 24,
});

const TitleTypography = styled(Typography)({
  fontSize: '45px',
  fontWeight: 500,
  color: theme.palette.custom.darkBrown,
  textAlign: 'center',
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(4),
});

const DescriptionTypography = styled(Typography)({
  fontSize: '16px',
  color: theme.palette.custom.mediumBrown,
  textAlign: 'center',
  maxWidth: '600px',
  paddingBottom: theme.spacing(4),
});

const DisclaimerTypography = styled(Typography)({
  fontSize: '16px',
  color: theme.palette.custom.mediumBrown,
  textAlign: 'center',
  maxWidth: '500px',
  paddingBottom: theme.spacing(9),
});

const ButtonStack = styled(Stack)({
  alignItems: 'center',
});
